import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './search-panel.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import { ifDefined } from 'lit-html/directives/if-defined';

export default class SearchPanel extends OutlineElement {
  static styles = [super.styles, componentStyles];

  static get properties() {
    return {
      contentType: { type: String },
      searchValue: { type: String },
      panelIsOpen: { type: Boolean },
      physicians: { type: Object },
      services: { type: Object },
      locations: { type: Object },
      fullSearch: { type: Object },
      suggestedOnly: { type: Boolean },
      mainMenuSearch: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.panelIsOpen = false;
    this.suggestedOnly = undefined;
    this.mainMenuSearch = false;
    this.maxResultsPerCol = 3;
    this.searchValue = '';
    this.physicians = {
      items: [],
      viewMoreUrl: 'https://providers.hf.org/search?unified=',
    };
    this.services = {
      items: [],
      viewMoreUrl: '/search?f[0]=content_type:departments_services&search=',
    };
    this.locations = {
      items: [],
      viewMoreUrl: '/search?f[0]=content_type:location&search=',
    };
    this.fullSearch = {
      items: [],
      viewMoreUrl: '/search?search=',
    };
  }

  highlightWord(string, words) {
    const regex = new RegExp(words, 'gi');
    return string.replace(regex, function (str) {
      return '<span class="search__panel__item--highlight">' + str + '</span>';
    });
  }

  renderItems(array, styleclass, highlight = false) {
    return array.slice(0, this.maxResultsPerCol).map(item => {
      let url;
      if (styleclass === 'full-search') {
        url = '/search?search=' + item.title;

        if (this.contentType) {
          url += '&f[0]=content_type:' + this.contentType;
        }
      } else {
        url = item.url;
      }

      return html`<li role="option">
        <a class="search__panel__item" href="${url}">
          ${highlight
            ? unsafeHTML(this.highlightWord(item.title, highlight))
            : item.title}
        </a>
      </li>`;
    });
  }

  render() {
    return html`
      <div
        aria-live="polite"
        class="${classMap({
          'search__panel': true,
          'search__panel--suggested-only': this.suggestedOnly,
          'search__panel--main-menu-search': this.mainMenuSearch,
          'search__panel--hidden':
            this.searchValue.length <= 2 || !this.panelIsOpen,
        })}"
      >
      <div class="search__panel--container">
        ${this.suggestedOnly
          ? nothing
          : html`
              <div class="search__panel--overlay-mobile"></div>
              <div class="search__results-grid">
                <hf-3-up-grid styleclass="search-panel">
                  <hf-search-panel-result
                    url="${this.physicians.viewMoreUrl}${this.searchValue}"
                    title="Doctors & Providers"
                  >
                    <ul>${this.renderItems(this.physicians.items)}</ul>
                  </hf-search-panel-result>

                  <hf-search-panel-result
                    url="${this.services.viewMoreUrl}${this.searchValue}"
                    title="Departments & Services"
                  >
                    <ul>${this.renderItems(this.services.items)}</ul>
                  </hf-search-panel-result>

                  <hf-search-panel-result
                    url="${this.locations.viewMoreUrl}${this.searchValue}"
                    title="Locations"
                  >
                    <ul>${this.renderItems(this.locations.items)}</ul>
                  </hf-search-panel-result>
                </hf-3-up-grid>
              </div>
            `}
        ${!this.fullSearch
          ? nothing
          : html`
              <hf-search-panel-result
                suggestedOnly="${ifDefined(this.suggestedOnly)}"
                styleclass="full-search"
                url="/search?search=${this.searchValue}"
                title="Suggested searches"
                .mainMenuSearch="${this.mainMenuSearch}"
              >
                <ul id="search__panel__results" role="listbox">
                  ${this.renderItems(
                    this.fullSearch.items,
                    'full-search',
                    this.searchValue
                  )}
                </ul>
              </hf-search-panel-result>
            `}
      </div>
    </div>
    `;
  }
}
customElements.define('hf-search-panel', SearchPanel);
